import {
  Component, Input, Output, EventEmitter,
  OnChanges, SimpleChanges, inject, signal, DestroyRef,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SensorHistoricalService, SensorHistoricalPoint } from '../../../core/services/sensor-historical.service';

interface SlotData { time: string; avgIn: number; avgOut: number; occ: number; }

@Component({
  selector: 'app-sensor-presence-chart',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="spc-card">

      <!-- En-tête -->
      <div class="spc-header" [class.spc-header--collapsed]="collapsed()">
        <div class="spc-title-block">
          <span class="spc-title">{{ 'previsions.ajustementManuel.sensorChart.title' | translate }}</span>
          <span class="spc-sub">{{ 'previsions.ajustementManuel.sensorChart.subtitle' | translate: { day: dayLabel() } }}</span>
        </div>
        <button class="spc-collapse-btn" (click)="collapsed.set(!collapsed())">
          <span class="spc-collapse-label">{{ (collapsed() ? 'previsions.ajustementManuel.sensorChart.show' : 'previsions.ajustementManuel.sensorChart.hide') | translate }}</span>
          <svg [class.rotated]="!collapsed()" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
            <path d="M6 9l6 6 6-6"/>
          </svg>
        </button>
      </div>

      <ng-container *ngIf="!collapsed()">

      <!-- Légende -->
      <div class="spc-legend">
        <span class="leg"><span class="leg-bar green"></span>{{ 'previsions.ajustementManuel.sensorChart.legendIn' | translate }}</span>
        <span class="leg"><span class="leg-bar red"></span>{{ 'previsions.ajustementManuel.sensorChart.legendOut' | translate }}</span>
        <span class="leg"><span class="leg-dash"></span>{{ 'previsions.ajustementManuel.sensorChart.legendOccupancy' | translate }}</span>
      </div>

      <!-- Chargement / vide -->
      <div *ngIf="loading()" class="spc-state">{{ 'previsions.ajustementManuel.sensorChart.loading' | translate }}</div>
      <div *ngIf="!loading() && !hasData()" class="spc-state">{{ 'previsions.ajustementManuel.sensorChart.empty' | translate }}</div>

      <!-- Graphique -->
      <div *ngIf="!loading() && hasData()" class="spc-plot-wrap" (mouseleave)="hovered.set(null)">
        <svg
          [attr.viewBox]="'0 0 ' + W + ' ' + H"
          preserveAspectRatio="none"
          class="spc-svg"
          (mousemove)="onMouseMove($event)">

          <!-- Grille horizontale (axe entrées/sorties — gauche) -->
          <g *ngFor="let t of yTicks()">
            <line x1="0" [attr.x2]="W" [attr.y1]="yBar(t)" [attr.y2]="yBar(t)"
                  stroke="#e5e7eb" stroke-width="1"/>
            <text [attr.x]="4" [attr.y]="yBar(t) - 4"
                  font-size="11" fill="#9ca3af">{{ t }}</text>
          </g>

          <!-- Barres vertes (avgIn) et rouges (avgOut) -->
          <ng-container *ngFor="let s of slots(); let i = index">
            <!-- Entrées (vert) -->
            <rect
              [attr.x]="i * colW + gap / 2"
              [attr.width]="colW / 2 - gap"
              [attr.y]="yBar(s.avgIn)"
              [attr.height]="chartBottom() - yBar(s.avgIn)"
              fill="#22c55e" rx="2"/>
            <!-- Sorties (rouge) -->
            <rect
              [attr.x]="i * colW + colW / 2"
              [attr.width]="colW / 2 - gap"
              [attr.y]="yBar(s.avgOut)"
              [attr.height]="chartBottom() - yBar(s.avgOut)"
              fill="#ef4444" rx="2"/>
          </ng-container>

          <!-- Ligne présence estimée (axe droit) -->
          <path [attr.d]="occPath()"
                fill="none"
                stroke="#513E9B"
                stroke-width="3"
                stroke-dasharray="8,4"
                stroke-linecap="round"
                opacity=".9"/>

          <!-- Labels Y droite (occupancy) -->
          <text *ngFor="let t of yTicksOcc()"
                [attr.x]="W - 4" [attr.y]="yOcc(t) - 3"
                text-anchor="end" font-size="11" fill="#a78bfa">{{ t }}</text>

          <!-- Curseur hover -->
          <rect *ngFor="let s of slots(); let i = index"
                [attr.x]="i * colW"
                [attr.y]="CHART_TOP"
                [attr.width]="colW"
                [attr.height]="chartHeight()"
                fill="transparent"
                class="hover-zone"
                (mouseenter)="hovered.set(i)"/>

          <!-- Indicateur hover -->
          <g *ngIf="hovered() !== null">
            <rect [attr.x]="hovered()! * colW" [attr.y]="CHART_TOP"
                  [attr.width]="colW" [attr.height]="chartHeight()"
                  fill="rgba(81,62,155,.06)"/>
            <circle [attr.cx]="(hovered()! + .5) * colW"
                    [attr.cy]="yOcc(slots()[hovered()!]?.occ ?? 0)"
                    r="5" fill="#513E9B" stroke="white" stroke-width="2"/>
          </g>

          <!-- Labels X (toutes les 2h) -->
          <text *ngFor="let l of xLabels()"
                [attr.x]="l.x" [attr.y]="H - 6"
                text-anchor="middle" font-size="11" fill="#6b7280">{{ l.label }}</text>

          <!-- Axe bas -->
          <line x1="0" [attr.y1]="chartBottom()" [attr.x2]="W" [attr.y2]="chartBottom()"
                stroke="#e5e7eb" stroke-width="1"/>
        </svg>

        <!-- Tooltip -->
        <div *ngIf="hovered() !== null" class="spc-tooltip"
             [class.flip]="hovered()! > 24"
             [style.left.%]="(hovered()! + .5) / 32 * 100">
          <div class="tt-time">{{ slots()[hovered()!]?.time }}</div>
          <div class="tt-in">↑ {{ $safeNavigationMigration(slots()[hovered()!]?.avgIn) | number:'1.1-1' }} {{ 'previsions.ajustementManuel.sensorChart.tooltipIn' | translate }}</div>
          <div class="tt-out">↓ {{ $safeNavigationMigration(slots()[hovered()!]?.avgOut) | number:'1.1-1' }} {{ 'previsions.ajustementManuel.sensorChart.tooltipOut' | translate }}</div>
          <div class="tt-occ">≈ {{ $safeNavigationMigration(slots()[hovered()!]?.occ) | number:'1.0-0' }} {{ 'previsions.ajustementManuel.sensorChart.tooltipOccupancy' | translate }}</div>
        </div>
      </div>

      </ng-container>

    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [`
    .spc-card {
      background: #fff;
      border: 1px solid #ede9f7;
      border-radius: 14px;
      overflow: hidden;
      margin-top: 20px;
    }

    .spc-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px 16px 8px;
      border-bottom: 1px solid #f5f3fb;

      &.spc-header--collapsed { border-bottom: none; padding-bottom: 12px; }
    }

    .spc-collapse-btn {
      display: flex;
      align-items: center;
      gap: 5px;
      padding: 5px 10px;
      border: 1px solid #ede9f7;
      border-radius: 20px;
      background: #f5f3fb;
      color: #513E9B;
      font-size: 12px;
      font-weight: 600;
      font-family: inherit;
      cursor: pointer;
      transition: background .15s, border-color .15s;

      &:hover { background: #ede9f7; border-color: #c4b5fd; }

      svg { transition: transform .2s; flex-shrink: 0; }
      svg.rotated { transform: rotate(180deg); }
    }

    .spc-collapse-label { line-height: 1; }

    .spc-title-block { display: flex; flex-direction: column; gap: 2px; }

    .spc-title {
      font-size: 14px;
      font-weight: 700;
      color: #513E9B;
    }

    .spc-sub {
      font-size: 11px;
      color: #9ca3af;
      font-weight: 500;
    }

    /* Légende */
    .spc-legend {
      display: flex;
      align-items: center;
      gap: 16px;
      padding: 6px 16px 4px;
      font-size: 11px;
      color: #6b7280;
    }

    .leg { display: flex; align-items: center; gap: 5px; }

    .leg-bar {
      display: inline-block;
      width: 12px; height: 10px;
      border-radius: 2px;
      &.green { background: #22c55e; }
      &.red   { background: #ef4444; }
    }

    .leg-dash {
      display: inline-block;
      width: 20px; height: 3px;
      background: repeating-linear-gradient(
        to right, #513E9B 0, #513E9B 5px, transparent 5px, transparent 8px
      );
    }

    /* Graphique */
    .spc-state {
      padding: 32px;
      text-align: center;
      color: #9ca3af;
      font-size: 13px;
    }

    .spc-plot-wrap {
      position: relative;
      padding: 0 0 0;
    }

    .spc-svg {
      display: block;
      width: 100%;
      height: auto;
    }

    .hover-zone { cursor: crosshair; }

    /* Tooltip */
    .spc-tooltip {
      position: absolute;
      top: 8px;
      transform: translateX(-50%);
      background: rgba(17, 24, 39, .92);
      color: #fff;
      border-radius: 8px;
      padding: 8px 12px;
      font-size: 12px;
      pointer-events: none;
      white-space: nowrap;
      z-index: 10;

      &.flip { transform: translateX(-90%); }
    }

    .tt-time { font-weight: 700; margin-bottom: 3px; color: #e5e7eb; }
    .tt-in   { color: #86efac; }
    .tt-out  { color: #fca5a5; }
    .tt-occ  { color: #c4b5fd; font-size: 11px; margin-top: 2px; border-top: 1px solid rgba(255,255,255,.15); padding-top: 3px; }

    /* Note bas */
    .spc-note {
      margin: 0;
      padding: 8px 16px 10px;
      font-size: 11px;
      color: #9ca3af;
      border-top: 1px solid #f5f3fb;
      line-height: 1.5;
    }
  `]
})
export class SensorPresenceChartComponent implements OnChanges {
  @Input() establishmentId: string | null | undefined;
  @Input() dayOfWeek = 1;
  @Output() dataAvailable = new EventEmitter<boolean>();
  @Output() dataPoints    = new EventEmitter<SensorHistoricalPoint[]>();

  private readonly svc = inject(SensorHistoricalService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  loading   = signal(false);
  hovered   = signal<number | null>(null);
  collapsed = signal(true);
  readonly showOccupancy = true;
  private raw = signal<SensorHistoricalPoint[]>([]);

  /* ── dimensions identiques au forecast-graph ── */
  readonly W          = 1000;
  readonly H          = 300;
  readonly axisH      = 24;   // zone basse (labels X)
  readonly CHART_TOP  = 24;   // padding haut (labels Y)
  readonly gap        = 2;
  readonly colW       = this.W / 32;

  /** Bas de la zone de tracé (avant labels X). */
  chartBottom(): number { return this.H - this.axisH; }
  /** Hauteur utile entre CHART_TOP et chartBottom. */
  chartHeight(): number { return this.chartBottom() - this.CHART_TOP; }

  private readonly DAY_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
  dayLabel(): string {
    const code = this.DAY_CODES[this.dayOfWeek % 7];
    return code ? this.translate.instant('common.days.shortByCode.' + code) : '';
  }

  hasData(): boolean { return this.raw().some(p => p.avgIn > 0 || p.avgOut > 0); }

  ngOnChanges(c: SimpleChanges): void {
    if ((c['establishmentId'] || c['dayOfWeek']) && this.establishmentId) {
      this.load();
    }
  }

  private load(): void {
    if (!this.establishmentId) return;
    this.loading.set(true);
    this.svc.getSensorHistoricalByEstablishment(this.establishmentId, this.dayOfWeek)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: r => {
          this.raw.set(r.points);
          this.loading.set(false);
          this.dataAvailable.emit(this.hasData());
          this.dataPoints.emit(r.points);
        },
        error: () => {
          this.raw.set([]);
          this.loading.set(false);
          this.dataAvailable.emit(false);
          this.dataPoints.emit([]);
        }
      });
  }

  /** 32 créneaux avec occupation cumulée. */
  slots(): SlotData[] {
    let occ = 0;
    return this.raw().map(p => {
      occ = Math.max(0, occ + p.avgIn - p.avgOut);
      return { time: p.time, avgIn: p.avgIn, avgOut: p.avgOut, occ: Math.round(occ * 10) / 10 };
    });
  }

  private maxFlow(): number {
    return Math.max(1, ...this.raw().map(p => Math.max(p.avgIn, p.avgOut)));
  }

  private maxOcc(): number {
    return Math.max(1, ...this.slots().map(s => s.occ));
  }

  yBar(v: number): number {
    const ratio = Math.min(v, this.maxFlow()) / this.maxFlow();
    return this.chartBottom() - ratio * this.chartHeight();
  }

  yOcc(v: number): number {
    const ratio = Math.min(v, this.maxOcc()) / this.maxOcc();
    return this.chartBottom() - ratio * this.chartHeight();
  }

  yTicks(): number[] {
    const max = this.maxFlow();
    const step = max <= 5 ? 1 : max <= 20 ? 5 : 10;
    const ticks: number[] = [];
    for (let t = step; t <= max; t += step) ticks.push(t);
    return ticks;
  }

  yTicksOcc(): number[] {
    const max = this.maxOcc();
    const step = max <= 10 ? 2 : max <= 50 ? 10 : 20;
    return [Math.round(max / 2), Math.round(max)];
  }

  occPath(): string {
    const s = this.slots();
    if (!s.length) return '';
    return s.map((p, i) => {
      const x = (i + 0.5) * this.colW;
      const y = this.yOcc(p.occ);
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  }

  xLabels(): { x: number; label: string }[] {
    return this.raw()
      .map((p, i) => ({ p, i }))
      .filter(({ p }) => p.time.endsWith(':00') && parseInt(p.time) % 2 === 0)
      .map(({ p, i }) => ({ x: (i + 0.5) * this.colW, label: p.time }));
  }

  onMouseMove(event: MouseEvent): void {
    const svg = event.currentTarget as SVGElement;
    const rect = svg.getBoundingClientRect();
    const ratio = (event.clientX - rect.left) / rect.width;
    const idx = Math.floor(ratio * 32);
    this.hovered.set(Math.max(0, Math.min(idx, 31)));
  }
}
